
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Commission, ReportDetail, TeacherMonthlyReport } from '@/types/salary';
import { useMutation, useQuery } from '@tanstack/react-query';

export function useTeacherMonthlySalary(selectedMonth: number, selectedYear: number) {
  const { toast } = useToast();

  const { data: teacherMonthlyReports, refetch } = useQuery({
    queryKey: ['teacher-monthly-reports', selectedMonth, selectedYear],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('teacher_monthly_reports')
        .select('*')
        .eq('month', selectedMonth)
        .eq('year', selectedYear)
        .order('teacher_id', { ascending: true });

      if (error) throw error;
      
      // Transform the data to ensure commission_details is properly typed
      return data.map(report => ({
        ...report,
        commission_details: report.commission_details 
          ? (report.commission_details as unknown as Commission[])
          : null
      })) as TeacherMonthlyReport[];
    }
  });

  const calculateMonthlyReportsMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_FUNCTION_URL || "https://lsbmvfjymydutatevugb.functions.supabase.co"}/calculate-teacher-monthly-reports`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify({ month: selectedMonth, year: selectedYear }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to calculate monthly reports');
      }

      return response.json();
    },
    onSuccess: () => {
      toast({
        title: 'คำนวณเงินเดือนครูสำเร็จ',
        description: `คำนวณรายงานประจำเดือน ${selectedMonth}/${selectedYear} เสร็จสิ้น`,
      });
      refetch();
    },
    onError: (error) => {
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: error.message,
        variant: 'destructive',
      });
    }
  });

  const calculateCommissionRatingMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const response = await fetch(
        `https://lsbmvfjymydutatevugb.functions.supabase.co/calculate-teacher-student-ratios`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify({ month: selectedMonth, year: selectedYear }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to calculate commission rating');
      }

      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: 'คำนวณ Commission Rating สำเร็จ',
        description: `คำนวณ Commission Rating สำหรับ ${data.students_processed} นักเรียน เสร็จสิ้น`,
      });
    },
    onError: (error) => {
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: error.message,
        variant: 'destructive',
      });
    }
  });

  const calculateClassTrialCostsMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const response = await fetch(
        `https://lsbmvfjymydutatevugb.functions.supabase.co/calculate-class-trial-costs`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify({ month: selectedMonth, year: selectedYear }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to calculate class trial costs');
      }

      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: 'คำนวณค่าใช้จ่ายทดลองเรียนสำเร็จ',
        description: `คำนวณค่าใช้จ่ายทดลองเรียน ${data.updatedCount || 0} รายการ เสร็จสิ้น`,
      });
    },
    onError: (error) => {
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: error.message,
        variant: 'destructive',
      });
    }
  });

  const calculateSingleTeacherReportMutation = useMutation({
    mutationFn: async (teacherId: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_FUNCTION_URL || "https://lsbmvfjymydutatevugb.functions.supabase.co"}/calculate-single-teacher-monthly-report`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify({ 
            teacherId,
            month: selectedMonth, 
            year: selectedYear 
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to calculate teacher report');
      }

      return response.json();
    },
    onSuccess: (data, teacherId) => {
      toast({
        title: 'คำนวณเงินเดือนสำเร็จ',
        description: `คำนวณรายงานของครู ${teacherId} เสร็จสิ้น`,
      });
      refetch();
    },
    onError: (error) => {
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: error.message,
        variant: 'destructive',
      });
    }
  });

  return {
    teacherMonthlyReports,
    calculateMonthlyReportsMutation,
    calculateCommissionRatingMutation,
    calculateClassTrialCostsMutation,
    calculateSingleTeacherReportMutation,
    refetch
  };
}
